import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, MapPin, Clock, Sparkles, Globe } from "lucide-react";

const clubFormSchema = z.object({
  clubName: z.string().min(2, "El nombre del club es requerido"),
  phone: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  website: z.string().url("URL inválida").optional().or(z.literal("")),
  address: z.string().min(1, "La dirección es requerida"),
  city: z.string().min(1, "La ciudad es requerida"),
  postalCode: z.string().optional(),
  numberOfCourts: z.number().min(1).max(50).default(1),
  courtTypes: z.array(z.string()).default([]),
  amenities: z.array(z.string()).default([]),
  openingTime: z.string().optional(),
  closingTime: z.string().optional(),
  openDays: z.array(z.string()).default([]),
  description: z.string().max(1000).optional(),
  instagramHandle: z.string().optional(),
  facebookUrl: z.string().url("URL inválida").optional().or(z.literal("")),
});

type ClubFormData = z.infer<typeof clubFormSchema>;

interface ClubOnboardingProps {
  onComplete: (data: ClubFormData) => void;
  isSubmitting?: boolean;
  clubEmail?: string;
}

const courtTypeOptions = [
  { id: "indoor", label: "Interior" },
  { id: "outdoor", label: "Exterior" },
  { id: "covered", label: "Cubierta" },
  { id: "panoramic", label: "Panorámica" },
];

const amenityOptions = [
  { id: "parking", label: "Parking" },
  { id: "showers", label: "Duchas" },
  { id: "locker_rooms", label: "Vestuarios" },
  { id: "pro_shop", label: "Tienda" },
  { id: "restaurant", label: "Restaurante" },
  { id: "bar", label: "Bar/Cafetería" },
  { id: "wifi", label: "WiFi" },
  { id: "rental", label: "Alquiler de material" },
  { id: "lessons", label: "Clases" },
  { id: "kids_area", label: "Zona infantil" },
];

const dayOptions = [
  { id: "monday", label: "Lunes" },
  { id: "tuesday", label: "Martes" },
  { id: "wednesday", label: "Miércoles" },
  { id: "thursday", label: "Jueves" },
  { id: "friday", label: "Viernes" },
  { id: "saturday", label: "Sábado" },
  { id: "sunday", label: "Domingo" },
];

export function ClubOnboarding({ onComplete, isSubmitting, clubEmail }: ClubOnboardingProps) {
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  const form = useForm<ClubFormData>({
    resolver: zodResolver(clubFormSchema),
    defaultValues: {
      clubName: "",
      email: clubEmail || "",
      numberOfCourts: 1,
      courtTypes: [],
      amenities: [],
      openDays: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
      openingTime: "08:00",
      closingTime: "22:00",
    },
  });

  const onSubmit = (data: ClubFormData) => {
    onComplete(data);
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">¡Bienvenido a CourtVibe!</CardTitle>
          <CardDescription>
            Configura tu club para empezar a conectar con jugadores de pádel
          </CardDescription>
          <div className="flex justify-center gap-2 mt-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-2 w-10 rounded-full transition-colors ${
                  i + 1 <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {step === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-lg font-medium mb-4">
                    <Building2 className="h-5 w-5 text-primary" />
                    <span>Información del Club</span>
                  </div>

                  <FormField
                    control={form.control}
                    name="clubName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre del club *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Ej: Pádel Club Santo Domingo" data-testid="input-club-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Teléfono</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="+1 809 000 0000" data-testid="input-club-phone" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email de contacto</FormLabel>
                          <FormControl>
                            <Input {...field} type="email" placeholder="info@club.com" data-testid="input-club-email" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sitio web</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input {...field} placeholder="https://www.tuclub.com" className="pl-10" data-testid="input-club-website" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-lg font-medium mb-4">
                    <MapPin className="h-5 w-5 text-primary" />
                    <span>Ubicación</span>
                  </div>

                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dirección *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Calle, número" data-testid="input-club-address" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ciudad *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Ej: Santo Domingo" data-testid="input-club-city" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="postalCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Código postal</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="28001" data-testid="input-club-postal" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="numberOfCourts"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número de pistas</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            min={1}
                            max={50}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                            data-testid="input-num-courts"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="courtTypes"
                    render={() => (
                      <FormItem>
                        <FormLabel>Tipos de pista</FormLabel>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          {courtTypeOptions.map((option) => (
                            <FormField
                              key={option.id}
                              control={form.control}
                              name="courtTypes"
                              render={({ field }) => (
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(option.id)}
                                      onCheckedChange={(checked) => {
                                        const current = field.value || [];
                                        if (checked) {
                                          field.onChange([...current, option.id]);
                                        } else {
                                          field.onChange(current.filter((v) => v !== option.id));
                                        }
                                      }}
                                      data-testid={`checkbox-court-${option.id}`}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">{option.label}</FormLabel>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-lg font-medium mb-4">
                    <Clock className="h-5 w-5 text-primary" />
                    <span>Horarios</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="openingTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Hora de apertura</FormLabel>
                          <FormControl>
                            <Input {...field} type="time" data-testid="input-opening-time" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="closingTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Hora de cierre</FormLabel>
                          <FormControl>
                            <Input {...field} type="time" data-testid="input-closing-time" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="openDays"
                    render={() => (
                      <FormItem>
                        <FormLabel>Días de apertura</FormLabel>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                          {dayOptions.map((option) => (
                            <FormField
                              key={option.id}
                              control={form.control}
                              name="openDays"
                              render={({ field }) => (
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(option.id)}
                                      onCheckedChange={(checked) => {
                                        const current = field.value || [];
                                        if (checked) {
                                          field.onChange([...current, option.id]);
                                        } else {
                                          field.onChange(current.filter((v) => v !== option.id));
                                        }
                                      }}
                                      data-testid={`checkbox-day-${option.id}`}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer text-sm">{option.label}</FormLabel>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {step === 4 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-lg font-medium mb-4">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <span>Servicios y Descripción</span>
                  </div>

                  <FormField
                    control={form.control}
                    name="amenities"
                    render={() => (
                      <FormItem>
                        <FormLabel>Servicios disponibles</FormLabel>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          {amenityOptions.map((option) => (
                            <FormField
                              key={option.id}
                              control={form.control}
                              name="amenities"
                              render={({ field }) => (
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(option.id)}
                                      onCheckedChange={(checked) => {
                                        const current = field.value || [];
                                        if (checked) {
                                          field.onChange([...current, option.id]);
                                        } else {
                                          field.onChange(current.filter((v) => v !== option.id));
                                        }
                                      }}
                                      data-testid={`checkbox-amenity-${option.id}`}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">{option.label}</FormLabel>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Descripción del club</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Cuéntanos sobre tu club, instalaciones, historia..."
                            className="resize-none"
                            rows={4}
                            data-testid="textarea-club-description"
                          />
                        </FormControl>
                        <FormDescription>
                          Máximo 1000 caracteres
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="instagramHandle"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Instagram</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="@tuclub" data-testid="input-instagram" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="facebookUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Facebook</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://facebook.com/tuclub" data-testid="input-facebook" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-4">
                {step > 1 ? (
                  <Button type="button" variant="outline" onClick={prevStep} data-testid="button-prev">
                    Anterior
                  </Button>
                ) : (
                  <div />
                )}

                {step < totalSteps ? (
                  <Button type="button" onClick={nextStep} data-testid="button-next">
                    Siguiente
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting} data-testid="button-submit">
                    {isSubmitting ? "Guardando..." : "Completar Registro"}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
